import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
} from '@tanstack/react-table';
import { Avatar, Button, Checkbox, EmptyState, PageScaffold } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@scripsteam/scrips-signal-ds';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useAuthStore } from '@/auth/auth-store';
import { usePermission } from '@/auth/permission-gate';
import { usePatientsList } from '@/patients/services/use-patients-list';
import { useArchivePatients } from '@/patients/services/use-archive-patients';
import { useMergePatients } from '@/patients/services/use-merge-patients';
import { useOrganisationInfo } from '@/patients/services/use-organisation-info';
import { usePatientsListStore } from '@/patients/store/patients-list-store';
import { ArchiveConfirmDialog } from '@/patients/components/archive-confirm-dialog';
import { PatientImportDialog } from '@/patients/components/patient-import-dialog';
import { PatientMergeDialog } from '@/patients/components/patient-merge-dialog';
import { PatientsArchiveToolbar } from '@/patients/components/patients-archive-toolbar';
import { PatientRowActions } from '@/patients/components/patient-row-actions';
import { buildCreateAppointmentPrefill } from '@/patients/lib/build-create-appointment-prefill';
import { useCreateAppointmentHandoff } from '@/shared/store/create-appointment-handoff';
import { FileUp, Search } from 'lucide-react';
import { SelectionGroup } from '@scripsteam/scrips-signal-ds';
import type { PatientDatum } from '@/types/patient-api';
import { V2_CARD_EDGE, V2_META, V2_PAGE_TITLE } from '@/shared/ui/design-v2';
import { isCohortEnrolmentOn } from '@/cohorts/lib/cohort-enrolment-switch';
import {
  EnrolInCohortDialog,
  type EnrolPatient,
} from '@/cohorts/components/enrol-in-cohort-dialog';
import { PageHeader } from '@/shared/ui/components/page-header';

/** Showcase page padding under the page header: 48px sides and bottom, 32px top. */
const SHOWCASE_PAGE_PAD = 'p-12 pt-8';

/**
 * Patient list landing screen. Server-paginated via /api/Patients/GetPatientList.
 * Search is debounced 400 ms; column sort is client-side on the visible page
 * (mirrors Flutter parity for v1 — see PROD-718). Archived/merged filter
 * chips persist per-user (PROD-719).
 *
 * Bulk archive (PROD-720): row checkboxes drive TanStack row-selection,
 * a toolbar above the table shows when 1+ rows are selected, Archive
 * fires `useArchivePatients` after a destructive confirmation dialog.
 * Selection is gated on the `Manage_Patients_Demographics` permission
 * with the `delete` flag (matches Flutter `patient_list_screen.dart:1467`
 * — the ticket said "edit" but code wins per Flutter parity).
 *
 * Out of scope (own tickets):
 * - Merge / Bulk import (PROD-721/722)
 * - Reusable PatientPicker variant (PROD-723)
 */

const DEFAULT_PAGE_SIZE = 25;

export function PatientsListPage() {
  const navigate = useNavigate();
  const orgId = useAuthStore((s) => s.user?.organizationId) ?? '';

  // The top bar's search opens this list already filtered: its term arrives in navigation state
  // (never in the URL), read on every arrival so a second search from this page applies too.
  const location = useLocation();
  const arrivedSearch = (location.state as { search?: string } | null)?.search;
  const [searchInput, setSearchInput] = useState(arrivedSearch ?? '');
  useEffect(() => {
    if (arrivedSearch) {
      setSearchInput(arrivedSearch);
      setPageNumber(1);
    }
  }, [location.key, arrivedSearch]);
  const debouncedSearch = useDebounce(searchInput, 400);
  const [pageNumber, setPageNumber] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  // Single-row archive (kebab menu). When set, the archive dialog + mutation
  // target this one patient instead of the bulk row-selection set.
  const [singleArchiveId, setSingleArchiveId] = useState<string | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  // PROD-3206 cohort enrolment: the patients the enrol dialog targets (null = closed). Behind
  // `isCohortEnrolmentOn()` only (the deploy flag AND this browser's `?x=cohorts`); no role gate (the cohort API enforces auth).
  const cohortEnrolment = isCohortEnrolmentOn();
  const [enrolTargets, setEnrolTargets] = useState<EnrolPatient[] | null>(null);
  // Where the dialog opened from: the toolbar (row selection) or one row's menu.
  const [enrolFromSelection, setEnrolFromSelection] = useState(false);

  const showArchived = usePatientsListStore((s) => s.showArchived);
  const showMerged = usePatientsListStore((s) => s.showMerged);
  const setShowArchived = usePatientsListStore((s) => s.setShowArchived);
  const setShowMerged = usePatientsListStore((s) => s.setShowMerged);

  // Flutter parity (patient_list_screen.dart:1467) — the toolbar action is
  // delete-flagged. Ticket says "edit" but code wins.
  const canArchive = usePermission(['Manage_Patients_Demographics'], {
    delete: true,
  });
  // Merge + Import both require `Manage_Patients_Demographics` with the
  // EDIT flag. Flutter parity:
  //   - Merge: `patient_list_bloc.dart` only dispatches
  //     `MergePatientsEvent` when `validatePermission([...], edit: true)` passes
  //   - Import: `patient_stack_widget.dart` doesn't render the import action
  //     without the same edit flag
  // Note vs. Archive above: Archive uses DELETE
  // (`patient_list_screen.dart:1467`), Merge + Import use EDIT — same
  // permission key, different capability flags.
  const canMerge = usePermission(['Manage_Patients_Demographics'], {
    edit: true,
  });
  const canImport = canMerge;
  // Import button is intentionally hidden right now (product request,
  // 2026-06-01) but the permission gate + dialog wiring are kept intact.
  // This is a feature flag, NOT a DOM removal: flip to `true` to restore
  // (the `canImport` gate below still applies, so admin-only re-show keeps
  // working without further code changes).
  const showImportButton = false;
  // Per-row kebab gating (Flutter `menuOptions()`,
  // patient_list_screen.dart:739). Edit/View title flips on the EDIT flag;
  // Archive reuses `canArchive` (DELETE flag) above.
  const canEditDemographics = canMerge;
  // View Billing Details gate. DELIBERATE divergence from the Flutter
  // row-action source (approved 2026-06-01): Flutter gates this menu item on
  // `Manage_Patient_Appointments` (add) at `patient_list_screen.dart:862` —
  // but that's a copy-paste of the "Create Appointment" item directly above
  // it, NOT a billing decision. The shortcut's actual destination — the
  // patient Billing tab — is gated on `Access_Patient_Billing` (view) in
  // BOTH Flutter (`create_patient_screen.dart:909`) and React
  // (`patient-edit-page.tsx:91`). Gating the shortcut on the same billing
  // permission as its destination makes it self-consistent (no shortcut to a
  // tab the user can't open, and no hidden shortcut for users who can) and
  // needs zero backend/Flutter coordination since the permission already
  // exists. This is the conversion-contract "fix the drift" path. Create
  // Appointment (deferred) will keep the `Manage_Patient_Appointments` gate.
  const canViewBilling = usePermission(['Access_Patient_Billing'], {
    view: true,
  });
  // Create Appointment gate — Flutter `patient_list_screen.dart:777-780`:
  // `Manage_Patient_Appointments` add (+ not merged, handled in the kebab).
  const canCreateAppointment = usePermission(['Manage_Patient_Appointments'], {
    add: true,
  });
  const userId = useAuthStore((s) => s.user?.userId) ?? '';
  const practiceId = useAuthStore((s) => s.user?.practiceId) ?? '';
  const orgLocations = useAuthStore((s) => s.user?.organizationLocations);
  const requestCreateAppointment = useCreateAppointmentHandoff(
    (s) => s.requestCreateAppointment,
  );
  // Org branding logo for the printable patient sticker (kebab → Print).
  // Flutter fetches org info (`FetchOrganisationDetailEvent`) and uses
  // `organisationInfo.brandingUrl` — NOT the login user's brandingUrl
  // (`patient_list_screen.dart:550`). Mirror that source here. Fetched once
  // at list level (cached 30m) so the logo is ready when the sticker opens;
  // React idiom prefetch vs Flutter's on-click fetch — same result.
  const { data: orgInfo } = useOrganisationInfo(orgId);
  const brandingUrl = orgInfo?.brandingUrl ?? null;
  // Sticker print-dimension testing block — Flutter gates on
  // `currentENV=="DEV" && isBeta`. "DEV env" in React = the local Vite dev
  // server (`import.meta.env.DEV`, empty base URL via proxy) OR the deployed
  // DEV build (`VITE_API_BASE_URL === dev-api`). Both are excluded from
  // QA/UAT/PROD builds (DEV flag false + non-dev base URL), so the block
  // never leaks outside DEV.
  const isDevEnv =
    import.meta.env.DEV ||
    import.meta.env.VITE_API_BASE_URL === 'https://dev-api.scrips.com';
  const isBeta = useAuthStore((s) => s.user?.isBeta) ?? false;
  const stickerDevTools = isDevEnv && isBeta;

  const archiveMutation = useArchivePatients();
  const mergeMutation = useMergePatients();

  const { data, isPending, isLoading, isError, refetch } = usePatientsList({
    organizationId: orgId,
    pageNumber,
    pageSize: DEFAULT_PAGE_SIZE,
    search: debouncedSearch || undefined,
    // Backend default already hides archived/merged — only opt IN when the
    // chip is on so we don't send a redundant ?IsArchived=false on every
    // request (would also defeat any backend-side caching keyed on the URL).
    isArchived: showArchived ? true : undefined,
    isMerged: showMerged ? true : undefined,
  });

  const rows = data?.patientData ?? [];
  const totalPages = data?.totalPage ?? 1;
  const totalCount = data?.count ?? 0;

  // Toolbar visibility — selection > 0 AND not on archived view (Flutter
  // hides bulk-archive when isArchived is on; the action wouldn't apply).
  const showArchiveToolbar = canArchive && !showArchived;

  // The row-select column is visible when ANY bulk action is available
  // for the user — Archive (canArchive + !showArchived) OR Merge (canMerge).
  // Cohort enrolment (flag on) also needs the selection, outside the archived view.
  const canBulkEnrol = cohortEnrolment && !showArchived;
  const showRowSelect = showArchiveToolbar || canMerge || canBulkEnrol;

  const columns = useMemo<ColumnDef<PatientDatum>[]>(
    () => {
      const cols: ColumnDef<PatientDatum>[] = [];
      if (showRowSelect) {
        cols.push({
          id: 'select',
          // Select-all checkbox in the header. DS `Checkbox` takes
          // `indeterminate` as a separate boolean alongside `checked`,
          // not a tri-state on `checked`.
          //
          // These checkboxes carry an INVISIBLE accessible name — there is no
          // adjacent visible text, so `label` (which renders visible content)
          // is not the right mechanism. The DS `Checkbox` had no `aria-*`
          // passthrough at all, which forced these names to be dropped when the
          // site first moved onto the DS. That gap was closed in the DS on
          // 2026-09-20 (`aria-label` / `aria-labelledby` forwarded to the
          // element carrying `role="checkbox"`), so the names are restored here
          // rather than approximated.
          header: ({ table }) => {
            const allSelected = table.getIsAllPageRowsSelected();
            const someSelected = table.getIsSomePageRowsSelected();
            return (
              <div onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  aria-label="Select all on this page"
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onCheckedChange={(v) =>
                    table.toggleAllPageRowsSelected(Boolean(v))
                  }
                />
              </div>
            );
          },
          cell: ({ row }) => (
            // stopPropagation so the row's onClick (navigate) doesn't fire
            // when the user toggles the row's checkbox.
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                aria-label={`Select ${
                  row.original.name ||
                  [
                    row.original.firstName,
                    row.original.middleName,
                    row.original.lastName,
                  ]
                    .filter(Boolean)
                    .join(' ') ||
                  'patient'
                }`}
                checked={row.getIsSelected()}
                onCheckedChange={(v) => row.toggleSelected(v === true)}
              />
            </div>
          ),
          enableSorting: false,
          size: 40,
        });
      }
      const mrnColumn: ColumnDef<PatientDatum> = {
        id: 'mrn',
        header: 'MRN',
        accessorFn: (row) => row.mrn ?? '—',
        cell: (info) => (
          <span
            className={V2_META}
          >
            {String(info.getValue() ?? '—')}
          </span>
        ),
      };
      // Showcase (prototype Patients list, design log 13 Oct): the person leads, with the photo, and the
      // MRN is the second column in both switch states (was: OFF first, ON last).
      const dataColumns: ColumnDef<PatientDatum>[] = [
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) =>
          row.name ||
          [row.firstName, row.middleName, row.lastName].filter(Boolean).join(' '),
        cell: (info) => (
          <span className="flex min-w-0 items-center gap-3">
            <Avatar
              size="sm"
              src={info.row.original.image || undefined}
              name={String(info.getValue() || '') || undefined}
            />
            <span className="text-sm font-medium">
              {String(info.getValue() || '—')}
            </span>
          </span>
        ),
      },
      mrnColumn,
      {
        id: 'dob',
        header: 'DOB',
        // Sort/filter still key off the DATE alone — sorting the rendered
        // "May 05, 2022 | 4y" string would be meaningless, and sorting the age
        // strings lexically puts 18d before 1y 3m before 7m.
        accessorFn: (row) => row.dob ?? formatBirthDate(row.birthDate),
        /**
         * PROD-1796 (client-reported) — the list showed DOB only; the old system
         * shows the exact age alongside it.
         *
         * `age` is SERVER-computed and already on this payload
         * (`GetPatientList` → `"age": "4y"`), so it is read rather than derived
         * locally: the backend already handles the units a naive year-subtraction
         * gets wrong — infants come back as months/days, not `0y`. React already
         * consumes this same field in `patient-sticker-dialog.tsx:71`.
         *
         * Rendered INLINE, pipe-separated, copied from Flutter
         * (`patient_list_screen.dart:205`):
         *
         *   "${birthDate == null ? "—" : scDateFormat(birthDate)} | ${age ?? "—"}"
         *
         * An earlier revision stacked the age beneath the date, on the in-app
         * calendar convention. That was the wrong reference for this surface: the
         * client asked for the list to match "the old system", which makes the old
         * system's shape the spec.
         *
         * The em-dash fallback is PER PART. Flutter always renders the separator
         * and an age slot, so a patient with no age reads `May 05, 2022 | —`
         * rather than dropping the segment — which also keeps the column's width
         * stable down the list.
         */
        cell: (info) => {
          const dob = String(info.getValue() ?? '—');
          // `|| '—'` not `?? '—'`: age arrives as '' / '   ' as well as null.
          const age = info.row.original.age?.trim() || '—';
          return (
            <span className="text-sm text-text-muted whitespace-nowrap">
              {dob} | {age}
            </span>
          );
        },
      },
      {
        id: 'gender',
        header: 'Gender',
        accessorFn: (row) => row.gender?.name ?? '—',
      },
      {
        id: 'phone',
        header: 'Phone',
        accessorFn: (row) => row.phone ?? row.contactNumber ?? '—',
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => row.isArchived === true,
        cell: (info) => {
          const archived = info.getValue() as boolean;
          // Showcase (design log 12 Oct, "Patients list calmer"): "Active" is the normal state,
          // so it is plain secondary text (it was a green pill on every row); Archived is muted
          // with a hollow mark.
          return archived ? (
            <span data-patient-status="archived" className="inline-flex items-center gap-2 text-text-muted">
              <span aria-hidden="true" className="size-2 shrink-0 rounded-full border border-current" />
              Archived
            </span>
          ) : (
            <span data-patient-status="active" className="text-text-secondary">
              Active
            </span>
          );
        },
      },
      ];
      return cols.concat(dataColumns, [
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        size: 48,
        cell: ({ row }) => (
          // stopPropagation so opening / clicking the menu doesn't fire the
          // row's navigate-to-edit onClick.
          <div
            className="flex justify-end"
            onClick={(e) => e.stopPropagation()}
          >
            <PatientRowActions
              patient={row.original}
              isArchivedView={showArchived}
              isMergedView={showMerged}
              canEditDemographics={canEditDemographics}
              canArchive={canArchive}
              canViewBilling={canViewBilling}
              canCreateAppointment={canCreateAppointment}
              brandingUrl={brandingUrl}
              stickerDevTools={stickerDevTools}
              onCreateAppointment={(p) => {
                // GUID-guard the first org location (empty id → null so the
                // panel's LocationSelector auto-picks; see prefill builder).
                const firstLoc = orgLocations?.[0]?.id
                  ? orgLocations[0]
                  : null;
                requestCreateAppointment(
                  buildCreateAppointmentPrefill(p, firstLoc, todayLocalISO()),
                );
              }}
              onEdit={(id) => navigate(`/patients/edit/${id}`)}
              onViewBilling={(id) =>
                navigate(`/patients/edit/${id}?tab=billing`)
              }
              onArchive={(id) => {
                setSingleArchiveId(id);
                setArchiveDialogOpen(true);
              }}
              onEnrolInCohort={
                cohortEnrolment
                  ? (p) => {
                      setEnrolFromSelection(false);
                      setEnrolTargets([toEnrolPatient(p)]);
                    }
                  : undefined
              }
            />
          </div>
        ),
      },
    ]);
    },
    [
      showRowSelect,
      showArchived,
      showMerged,
      canEditDemographics,
      canArchive,
      canViewBilling,
      canCreateAppointment,
      cohortEnrolment,
      brandingUrl,
      stickerDevTools,
      orgLocations,
      requestCreateAppointment,
      navigate,
    ],
  );

  const selectedRows = useMemo(
    () =>
      rows.filter(
        (r) => r.patientId && rowSelection[r.patientId] === true,
      ),
    [rows, rowSelection],
  );
  const selectedCount = selectedRows.length;

  const handleArchive = () => {
    // Single-row kebab archive targets one id; otherwise bulk row-selection.
    const ids = singleArchiveId
      ? [singleArchiveId]
      : selectedRows
          .map((r) => r.patientId)
          .filter((id): id is string => Boolean(id));
    if (ids.length === 0) {
      setArchiveDialogOpen(false);
      setSingleArchiveId(null);
      return;
    }
    archiveMutation.mutate(
      { patientIds: ids },
      {
        onSuccess: () => {
          setArchiveDialogOpen(false);
          setSingleArchiveId(null);
          setRowSelection({});
          toast.success(
            ids.length === 1
              ? 'Patient archived'
              : `${ids.length} patients archived`,
          );
        },
      },
    );
  };

  const handleMerge = (parentPatient: string, childPatients: string[]) => {
    mergeMutation.mutate(
      { parentPatient, childPatients },
      {
        onSuccess: () => {
          setMergeDialogOpen(false);
          setRowSelection({});
          toast.success(
            `Merged ${childPatients.length} record${childPatients.length === 1 ? '' : 's'} into parent`,
          );
          // Flutter `patient_list_bloc.dart` routes to the parent's edit
          // screen on success — mirrors that here.
          navigate(`/patients/edit/${parentPatient}`);
        },
      },
    );
  };

  const table = useReactTable({
    data: rows,
    columns,
    // `getRowId` makes selection survive list refetches — without it, row
    // index is the key and a refetch (e.g. on filter toggle) drops state.
    // Patients without a patientId can't be selected; index fallback keeps
    // TanStack happy for those rare rows.
    getRowId: (row, idx) => row.patientId ?? `idx-${idx}`,
    state: { sorting, rowSelection },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: (row) => Boolean(row.original.patientId),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
  });

  return (
    <PageScaffold
      variant="list"
      // ON: the page on the frame's white (export 23 Oct `PaPatientsListScreen`: the body paints no background of its
      // own; the grey canvas went, Ahmed 23 Oct), title text-primary on the DS scale, the count as quiet meta, and
      // the table card on the card border + shadow-2. OFF passes nothing new.
      // Showcase: content sits at 48px sides / 32px top / 48px bottom (`p-12 pt-8` replaces the
      // scaffold's `p-6`), and the page header (zone contract Z4) rides the scaffold's `header`
      // slot pulled back out to the frame's edges by the matching negative margins, so its
      // hairline runs full width. `mb-4` + the scaffold's `gap-4` keep 32px above the filters.
      className={`patients-list ${SHOWCASE_PAGE_PAD}`}
      bodyProps={{ className: V2_CARD_EDGE }}
      headerProps={{ className: '-mx-12 -mt-8 mb-4' }}
      header={
        <PageHeader
          title={<span className={V2_PAGE_TITLE}>Patients</span>}
          meta={<span className={V2_META}>{`${totalCount.toLocaleString()} total`}</span>}
          actions={
            <>
              {/* Hidden via `showImportButton` flag (see decl). `canImport`
                  gate retained so flipping the flag restores correct gating. */}
              {showImportButton && canImport ? (
                <Button
                  variant="secondary"
                  onClick={() => setImportDialogOpen(true)}
                >
                  <FileUp className="size-4 mr-1" />
                  Import
                </Button>
              ) : null}
              <Button onClick={() => navigate('/patients/new')}>New patient</Button>
            </>
          }
        />
      }
      subheader={
        <>
          {/* One list table (design log 13 Oct, Rule): the search is the toolbar above the card, the
              filter chips sit beside it, and the card below is the table with its count line. */}
          <div data-slot="patients-toolbar">
            <TextField
              aria-label="Filter this list"
              placeholder="Filter by name, MRN or phone"
              leadingIcon={<Search className="size-4" aria-hidden />}
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPageNumber(1);
              }}
            />
            {/* Two independent boolean filter toggles ("archived", "merged")
                modelled as one Signal SelectionGroup (multi-select chip group,
                PROD-1034). The value array is the set of active toggles;
                membership ⇔ the boolean each chip used to carry. onChange diffs
                the next array against current state so each dimension's setter
                fires with its real next value, then runs the shared side-effects
                (reset page, clear selection — a filter change can drop selected
                rows from the page, so a stale row must not get archived out from
                under the user). Migrated from two `FilterChip` atoms (D-012). */}
            <SelectionGroup
              aria-label="Patient list filters"
              options={[
                { value: 'archived', label: 'Show archived' },
                { value: 'merged', label: 'Show merged' },
              ]}
              value={[
                ...(showArchived ? ['archived'] : []),
                ...(showMerged ? ['merged'] : []),
              ]}
              onChange={(next) => {
                const nextArchived = next.includes('archived');
                const nextMerged = next.includes('merged');
                if (nextArchived !== showArchived) setShowArchived(nextArchived);
                if (nextMerged !== showMerged) setShowMerged(nextMerged);
                setPageNumber(1);
                setRowSelection({});
              }}
            />
          </div>

          {selectedCount > 0 && (showArchiveToolbar || canMerge || canBulkEnrol) ? (
            <PatientsArchiveToolbar
              count={selectedCount}
              onClear={() => setRowSelection({})}
              onArchive={
                showArchiveToolbar
                  ? () => {
                      setSingleArchiveId(null);
                      setArchiveDialogOpen(true);
                    }
                  : undefined
              }
              onMerge={
                canMerge && selectedCount >= 2
                  ? () => setMergeDialogOpen(true)
                  : undefined
              }
              onEnrol={
                canBulkEnrol
                  ? () => {
                      setEnrolFromSelection(true);
                      setEnrolTargets(selectedRows.map(toEnrolPatient));
                    }
                  : undefined
              }
              isPending={archiveMutation.isPending || mergeMutation.isPending}
            />
          ) : null}
        </>
      }
      footer={`Page ${pageNumber} of ${totalPages}`}
      footerActions={
        <>
          <Button
            variant="secondary"
            size="sm"
            disabled={pageNumber <= 1 || isLoading}
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={pageNumber >= totalPages || isLoading}
            onClick={() => setPageNumber((p) => p + 1)}
          >
            Next
          </Button>
        </>
      }
      overlay={
        <>
          <ArchiveConfirmDialog
            open={archiveDialogOpen}
            onOpenChange={(open) => {
              setArchiveDialogOpen(open);
              // Clear the single-row target on close so a later bulk archive
              // shows the correct selected-count copy.
              if (!open) setSingleArchiveId(null);
            }}
            count={singleArchiveId ? 1 : selectedCount}
            onConfirm={handleArchive}
            isPending={archiveMutation.isPending}
          />

          <PatientMergeDialog
            open={mergeDialogOpen}
            onOpenChange={setMergeDialogOpen}
            selected={selectedRows}
            onConfirm={handleMerge}
            isPending={mergeMutation.isPending}
          />

          {cohortEnrolment ? (
            <EnrolInCohortDialog
              open={enrolTargets !== null}
              onClose={() => setEnrolTargets(null)}
              // Clear the selection only after an enrol from the toolbar got a server answer.
              // Cancel, or an enrol from one row's menu, leaves the selection alone.
              onEnrolled={enrolFromSelection ? () => setRowSelection({}) : undefined}
              patients={enrolTargets ?? []}
            />
          ) : null}

          <PatientImportDialog
            open={importDialogOpen}
            onOpenChange={setImportDialogOpen}
            organizationId={orgId}
            practiceId={practiceId}
            createdBy={userId}
          />
        </>
      }
    >
      <div data-slot="list-card-title">
        <h2>All patients</h2>
        {data ? (
          <span data-slot="list-card-count">
            {`Showing ${rows.length.toLocaleString()} of ${totalCount.toLocaleString()} ${totalCount === 1 ? 'patient' : 'patients'}`}
          </span>
        ) : null}
      </div>
      {/* Gate on the query's own pending state, not isLoading: a query
          disabled by `enabled: !!orgId` (session not resolved yet) is
          status:'pending' / isPending:true but isLoading:false, so isLoading
          would let the list fall through to the "No patients found" empty
          state. isPending holds the skeleton until the query resolves; a
          seeded cache reads as 'success' immediately, so Storybook shows
          rows with no flash. */}
      {isPending ? (
        <ListSkeleton />
      ) : isError ? (
        // §2.5 / §1 States: the one error pattern, DS EmptyState (it has no tone prop).
        <EmptyState
          icon="warning"
          title="Couldn't load patients."
          ctaLabel="Retry"
          onCtaClick={() => refetch()}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="patient"
          title={`No patients found${debouncedSearch ? ` matching "${debouncedSearch}"` : ''}.`}
          description={
            debouncedSearch
              ? undefined
              : 'Click “New Patient” to add the first one.'
          }
        />
      ) : (
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((group) => (
              <TableRow key={group.id}>
                {group.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    // Look hooks only (design DataTable `.selectCell` / `.actionsCell` / `.head[data-sortable]`):
                    // the bridge sizes the select and actions columns and greys a sortable header to body ink on hover.
                    data-col={header.column.id}
                    data-sortable={header.column.getCanSort() ? '' : undefined}
                    onClick={header.column.getToggleSortingHandler()}
                    className="cursor-pointer select-none"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {{ asc: ' ↑', desc: ' ↓' }[header.column.getIsSorted() as string] ?? null}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                onClick={() =>
                  row.original.patientId &&
                  navigate(`/patients/edit/${row.original.patientId}`)
                }
                // A ticked row reads as selected (design DataTable `.row[data-selected]`); the DS row keys its tint
                // off `data-state`, the bridge paints it the new-row blue. Hover is the bridge's panel grey.
                data-state={row.getIsSelected() ? 'selected' : undefined}
                className="cursor-pointer"
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} data-col={cell.column.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </PageScaffold>
  );
}

function ListSkeleton() {
  return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}


/** The enrol dialog's view of a list row (rows without a patientId are never selectable). */
function toEnrolPatient(p: PatientDatum): EnrolPatient {
  return {
    id: p.patientId ?? '',
    name: p.name || [p.firstName, p.middleName, p.lastName].filter(Boolean).join(' ') || 'Patient',
    mrn: p.mrn ?? null,
  };
}

/** Flutter sends birthDate as ISO; format defensively if `dob` (formatted) is missing. */
/** Local YYYY-MM-DD (avoids the toISOString UTC shift near midnight). */
function todayLocalISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatBirthDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}
